import React, { useState, useEffect, useMemo } from "react";
import {
  getFirestore,
  collection,
  query,
  getDocs,
  doc,
  updateDoc,
  getDoc,
} from "firebase/firestore";
import {
  CheckCircle,
  AlertCircle,
  Clock,
  Activity,
  User,
  Calendar,
  ChevronDown,
  ChevronRight,
  Filter,
  ArrowUp,
  ArrowDown,
  Briefcase,
  ListTodo,
  DollarSign,
  Check,
  X,
  Copy,
} from "lucide-react";
import { buildTasksSearchText } from "../utils/tasksSearchText";
import { evaluateTasks } from "../utils/status";
import { useTheme } from "../ThemeContext";
import { usePermissions } from "../permissions/PermissionsProvider";
import { ACTIONS } from "../permissions/definition";
import HeaderActionsMinimal from "./HeaderActionsMinimal";

// ==================== HELPER COMPONENTS ====================
const StatCard = ({ title, value, icon, color }) => (
  <div className="group relative bg-white/[0.08] backdrop-blur-xl rounded-2xl p-5 border border-white/[0.15] min-w-[140px] overflow-hidden transition-all duration-300 hover:bg-white/[0.12] hover:border-white/25 hover:scale-[1.02] hover:shadow-lg">
    {/* Subtle gradient overlay */}
    <div className="absolute inset-0 bg-gradient-to-br from-white/[0.05] to-transparent pointer-events-none" />

    {/* Content */}
    <div className="relative z-10">
      <div className="flex items-center gap-2.5 mb-2">
        <div
          className={`w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center`}
        >
          <span className="text-white/80">{icon}</span>
        </div>
        <span className="text-[11px] font-semibold text-white/70 uppercase tracking-widest">
          {title}
        </span>
      </div>
      <p className="text-3xl font-bold text-white tracking-tight">{value}</p>
    </div>
  </div>
);

const FilterSection = ({ title, children, noBorder, balanced }) => {
  const containerClasses = balanced
    ? "pt-3 pb-2 mb-0"
    : !noBorder
      ? "pb-5 mb-5 border-b border-gray-100"
      : "pb-5 mb-4";

  return (
    <div className={containerClasses}>
      <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
        <span className="w-1 h-1 rounded-full bg-gray-300"></span>
        {title}
      </h4>
      {children}
    </div>
  );
};

const CheckboxItem = ({ label, checked, onChange, count, theme }) => (
  <label className="flex items-center justify-between py-2 px-2.5 rounded-lg hover:bg-gray-50/80 cursor-pointer transition-all duration-150 group">
    <div className="flex items-center gap-2.5">
      <div
        className={`w-4 h-4 rounded flex items-center justify-center border transition-all duration-150 ${checked
          ? "border-transparent"
          : "border-gray-300 group-hover:border-gray-400"
          }`}
        style={
          checked
            ? { backgroundColor: theme?.filterCheckboxChecked || "#6366f1" }
            : {}
        }
      >
        {checked && (
          <svg
            className="w-2.5 h-2.5 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={3}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 13l4 4L19 7"
            />
          </svg>
        )}
      </div>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="sr-only"
      />
      <span
        className={`text-[12px] leading-[1.05rem] transition-colors duration-150 ${checked
          ? "text-gray-900 font-medium"
          : "text-gray-600 group-hover:text-gray-800"
          }`}
      >
        {label}
      </span>
    </div>
    {count !== undefined && (
      <span
        className={`text-[10px] font-medium px-2 py-0.5 rounded-full transition-colors duration-150 ${checked ? "text-white" : "bg-gray-100 text-gray-500"
          }`}
        style={
          checked
            ? { backgroundColor: theme?.filterCheckboxChecked || "#6366f1" }
            : {}
        }
      >
        {count}
      </span>
    )}
  </label>
);

const circleFlagCodeMap = {
  "UK": "gb",
  "UNITED KINGDOM": "gb",
  "USA": "us",
  "UNITED STATES": "us",
  "GERMANY": "de",
  "FRANCE": "fr",
  "JAPAN": "jp",
  "CHINA": "cn",
  "INDIA": "in",
  "BRAZIL": "br",
  "CANADA": "ca",
  "AUSTRALIA": "au",
  "ITALY": "it",
  "SPAIN": "es",
  "RUSSIA": "ru",
  "SOUTH KOREA": "kr",
  "NETHERLANDS": "nl",
  "TURKEY": "tr",
  "SAUDI ARABIA": "sa",
  "SWITZERLAND": "ch",
  "SWEDEN": "se",
  "POLAND": "pl",
  "BELGIUM": "be",
  "NORWAY": "no",
  "AUSTRIA": "at",
  "DENMARK": "dk",
  "FINLAND": "fi",
  "IRELAND": "ie",
  "NEW ZEALAND": "nz",
  "SINGAPORE": "sg",
  "UAE": "ae",
  "UNITED ARAB EMIRATES": "ae",
  "UAE - ABU DHABI": "ae",
  "UAE - AJMAN": "ae",
  "UAE - DUBAI": "ae",
  "UAE - FUJAIRAH": "ae",
  "UAE - RAK": "ae",
  "UAE - SHARJAH": "ae",
  "UAE - UAQ": "ae",
  "ISRAEL": "il",
  "SOUTH AFRICA": "za",
  "MEXICO": "mx",
  "EGYPT": "eg",
  "MOROCCO": "ma",
  "ALGERIA": "dz",
  "TUNISIA": "tn",
  "LIBYA": "ly",
  "QATAR": "qa",
  "KUWAIT": "kw",
  "BAHRAIN": "bh",
  "OMAN": "om",
  "JORDAN": "jo",
  "LEBANON": "lb",
  "IRAQ": "iq",
  "YEMEN": "ye",
  "SYRIA": "sy",
  "PALESTINE": "ps",
  "IRAN": "ir",
  "PAKISTAN": "pk",
  "AFGHANISTAN": "af",
  "DJIBOUTI": "dj",
  "MAURITANIA": "mr",
  "SOMALIA": "so",
  "SUDAN": "sd",
};

const getCircleFlagCode = (countryOrCompany) => {
  if (!countryOrCompany) return "xx";
  const normalized = countryOrCompany.trim().toUpperCase();

  if (/^[A-Z]{2}$/.test(normalized)) {
    return normalized.toLowerCase();
  }

  return circleFlagCodeMap[normalized] || "xx";
};

const getCircleFlagUrl = (countryOrCompany) =>
  `https://hatscripts.github.io/circle-flags/flags/${getCircleFlagCode(
    countryOrCompany
  )}.svg`;

const CountryFlagIcon = ({
  country,
  size = 18,
  className = "",
  ...imgProps
}) => (
  <img
    src={getCircleFlagUrl(country)}
    alt={country ? `${country} flag` : "Flag"}
    width={size}
    height={size}
    className={`flex-shrink-0 ${className}`}
    style={{
      borderRadius: "50%",
      boxShadow: "0 2px 6px rgba(0, 0, 0, 0.12)",
      border: "1px solid rgba(15, 23, 42, 0.08)",
      backgroundColor: "#fff",
    }}
    loading="lazy"
    {...imgProps}
  />
);

// Group consecutive tasks by projectRef
const groupConsecutiveTasksByProject = (tasks) => {
  if (!tasks || tasks.length === 0) return [];

  const groups = [];
  let currentGroup = {
    projectRef: tasks[0].projectRef,
    projectId: tasks[0].projectId,
    tasks: [tasks[0]],
  };

  for (let i = 1; i < tasks.length; i++) {
    if (tasks[i].projectRef === currentGroup.projectRef) {
      currentGroup.tasks.push(tasks[i]);
    } else {
      groups.push(currentGroup);
      currentGroup = {
        projectRef: tasks[i].projectRef,
        projectId: tasks[i].projectId,
        tasks: [tasks[i]],
      };
    }
  }
  groups.push(currentGroup);

  return groups;
};

// ==================== MAIN COMPONENT ====================
const Tasks = ({ user }) => {
  const getDefaultAnalyst = (email) => {
    const mapping = {
      "mustafa@fortcorps.com": "Mustafa",
      "aseel@fortcorps.com": "Aseel",
      "sadegh@fortcorps.com": "Sadegh",
      "muhab@fortcorps.com": "Muhab",
      "omar@fortcorps.com": "Omar",
      "zak@fortcorps.com": "Zak",
    };
    return mapping[email] || "All";
  };

  const defaultAnalyst = getDefaultAnalyst(user.email);
  const [analyst, setAnalyst] = useState(defaultAnalyst);
  const [tasks, setTasks] = useState([]);
  const [baseTasks, setBaseTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeProjectManager, setIncludeProjectManager] = useState(false);
  const [expandedTasks, setExpandedTasks] = useState({});
  const [editingCompletedOn, setEditingCompletedOn] = useState({
    projectId: null,
    taskNo: null,
    value: "",
  });
  const [sortColumn, setSortColumn] = useState("dueDate");
  const [sortOrder, setSortOrder] = useState("asc");
  const [selectedStatuses, setSelectedStatuses] = useState({
    "In Progress": true,
    Attention: true,
    Completed: true,
    Overdue: true,
  });

  const taskOptions = [
    "DSS",
    "BC",
    "SR",
    "PS",
    "VS",
    "IMEQ",
    "BEQ",
    "FL",
    "SS",
    "EQ",
    "LC",
    "MA",
    "RPS",
    "SV",
    "VES",
  ];

  const buildDefaultTaskSelection = () => {
    const initialTasks = {};
    taskOptions.forEach((task) => {
      initialTasks[task] = true;
    });
    return initialTasks;
  };

  const [selectedTasks, setSelectedTasks] = useState(() =>
    buildDefaultTaskSelection()
  );
  const [selectedCountries, setSelectedCountries] = useState({});

  const db = getFirestore();
  const { theme } = useTheme();
  const { can } = usePermissions();
  const openProjectInNewTab = (projectId) => {
    if (!projectId) return;
    window.open(`/project/${projectId}`, "_blank", "noopener,noreferrer");
  };

  // Load saved filters from localStorage
  useEffect(() => {
    const savedStatuses = localStorage.getItem("taskSelectedStatuses");
    if (savedStatuses) {
      setSelectedStatuses(JSON.parse(savedStatuses));
    }

    const savedTasks = localStorage.getItem("taskSelectedTasks");
    if (savedTasks) {
      try {
        const parsedTasks = JSON.parse(savedTasks);
        setSelectedTasks({ ...buildDefaultTaskSelection(), ...parsedTasks });
      } catch (error) {
        console.error("Failed to parse saved task filters", error);
        setSelectedTasks(buildDefaultTaskSelection());
      }
    }

    const savedCountries = localStorage.getItem("taskSelectedCountries");
    if (savedCountries) {
      try {
        setSelectedCountries(JSON.parse(savedCountries));
      } catch (error) {
        console.error("Failed to parse saved country filters", error);
      }
    }

    const savedIncludeProjectManager = localStorage.getItem(
      "taskIncludeProjectManager"
    );
    if (savedIncludeProjectManager) {
      try {
        setIncludeProjectManager(JSON.parse(savedIncludeProjectManager));
      } catch (error) {
        console.error(
          "Failed to parse saved include project manager preference",
          error
        );
      }
    }
  }, []);

  // Fetch tasks
  useEffect(() => {
    const fetchTasks = async () => {
      setLoading(true);
      try {
        const projectsQuery = query(collection(db, "projects"));
        const querySnapshot = await getDocs(projectsQuery);
        let fetchedTasks = [];
        const updatePromises = [];

        for (const docSnap of querySnapshot.docs) {
          const projectData = docSnap.data();
          const projectTasks = Array.isArray(projectData.tasks)
            ? projectData.tasks
            : [];

          if (projectTasks.length === 0) continue;

          const { updatedTasks } = evaluateTasks(projectTasks);
          const tasksChanged =
            projectTasks.length !== updatedTasks.length ||
            updatedTasks.some(
              (task, index) =>
                task.taskStatus !==
                (projectTasks[index] && projectTasks[index].taskStatus)
            );

          if (tasksChanged) {
            const projectRef = doc(db, "projects", docSnap.id);
            updatePromises.push(
              updateDoc(projectRef, {
                tasks: updatedTasks,
                tasksSearchText: buildTasksSearchText(updatedTasks),
              })
            );
          }

          updatedTasks.forEach((task) => {
            const matchesAnalyst =
              analyst === "All" ||
              task.analyst1 === analyst ||
              task.analyst2 === analyst ||
              (includeProjectManager &&
                projectData.projectManager === analyst);

            if (matchesAnalyst) {
              fetchedTasks.push({
                ...task,
                projectRef: projectData.projectRef,
                projectId: docSnap.id,
                client: projectData.client,
                poc: projectData.poc,
                projectManager: projectData.projectManager,
              });
            }
          });
        }

        if (updatePromises.length) {
          await Promise.allSettled(updatePromises);
        }

        const unfilteredTasks = [...fetchedTasks];

        // Filter by status
        let filteredTasks = fetchedTasks.filter(
          (task) => selectedStatuses[task.taskStatus]
        );

        // Filter by task type
        filteredTasks = filteredTasks.filter(
          (task) => selectedTasks[task.task] ?? true
        );

        // Filter by country (if user has interacted with country filter)
        // Tasks with empty/missing country fields always pass through
        const hasCountrySelection = Object.keys(selectedCountries).length > 0;
        if (hasCountrySelection) {
          filteredTasks = filteredTasks.filter(
            (task) => !task.country || selectedCountries[task.country]
          );
        }

        // Sort tasks
        filteredTasks.sort((a, b) => {
          let aValue = a[sortColumn];
          let bValue = b[sortColumn];
          if (sortColumn === "projectRef") {
            const aParts = aValue.split("-").map((part) => part.replace("P", ""));
            const bParts = bValue.split("-").map((part) => part.replace("P", ""));
            const aYear = parseInt(aParts[1], 10);
            const bYear = parseInt(bParts[1], 10);
            const aNumber = parseInt(aParts[0], 10);
            const bNumber = parseInt(bParts[0], 10);
            return sortOrder === "asc"
              ? aYear - bYear || aNumber - bNumber
              : bYear - aYear || bNumber - aNumber;
          }
          if (sortOrder === "asc") {
            return aValue > bValue ? 1 : -1;
          } else {
            return aValue < bValue ? 1 : -1;
          }
        });

        setBaseTasks(unfilteredTasks);
        setTasks(filteredTasks);
      } catch (error) {
        console.error("Failed to fetch tasks", error);
        setBaseTasks([]);
        setTasks([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTasks();
  }, [analyst, db, includeProjectManager, selectedCountries, selectedStatuses, selectedTasks, sortColumn, sortOrder]);

  // Handlers
  const handleAnalystChange = (event) => {
    setAnalyst(event.target.value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleIncludeProjectManagerChange = (event) => {
    const newValue = event.target.checked;
    setIncludeProjectManager(newValue);
    localStorage.setItem(
      "taskIncludeProjectManager",
      JSON.stringify(newValue)
    );
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleStatusChange = (status) => {
    const updatedStatuses = {
      ...selectedStatuses,
      [status]: !selectedStatuses[status],
    };
    setSelectedStatuses(updatedStatuses);
    localStorage.setItem(
      "taskSelectedStatuses",
      JSON.stringify(updatedStatuses)
    );
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleTaskChange = (task) => {
    const updatedTasks = {
      ...selectedTasks,
      [task]: !selectedTasks[task],
    };
    setSelectedTasks(updatedTasks);
    localStorage.setItem("taskSelectedTasks", JSON.stringify(updatedTasks));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSelectAllTasks = () => {
    const allSelectedTasks = {};
    taskOptions.forEach((task) => {
      allSelectedTasks[task] = true;
    });
    setSelectedTasks(allSelectedTasks);
    localStorage.setItem("taskSelectedTasks", JSON.stringify(allSelectedTasks));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeselectAllTasks = () => {
    const noneSelectedTasks = {};
    taskOptions.forEach((task) => {
      noneSelectedTasks[task] = false;
    });
    setSelectedTasks(noneSelectedTasks);
    localStorage.setItem(
      "taskSelectedTasks",
      JSON.stringify(noneSelectedTasks)
    );
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCountryChange = (country) => {
    const updatedCountries = {
      ...selectedCountries,
      [country]: !selectedCountries[country],
    };
    setSelectedCountries(updatedCountries);
    localStorage.setItem("taskSelectedCountries", JSON.stringify(updatedCountries));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSelectAllCountries = () => {
    const allSelectedCountries = {};
    availableCountries.forEach((country) => {
      allSelectedCountries[country] = true;
    });
    setSelectedCountries(allSelectedCountries);
    localStorage.setItem("taskSelectedCountries", JSON.stringify(allSelectedCountries));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeselectAllCountries = () => {
    const noneSelectedCountries = {};
    availableCountries.forEach((country) => {
      noneSelectedCountries[country] = false;
    });
    setSelectedCountries(noneSelectedCountries);
    localStorage.setItem("taskSelectedCountries", JSON.stringify(noneSelectedCountries));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleTaskExpanded = (taskId) => {
    setExpandedTasks((prev) => ({
      ...prev,
      [taskId]: !prev[taskId],
    }));
  };

  const fetchAndRefreshTask = async (projectId, taskNo) => {
    const projectDocRef = doc(db, "projects", projectId);
    const projectDoc = await getDoc(projectDocRef);

    if (!projectDoc.exists()) {
      console.error("Project document does not exist");
      return;
    }

    const projectData = projectDoc.data();
    if (!projectData.tasks) {
      console.error("Project data does not contain tasks");
      return;
    }

    const projectTasks = Array.isArray(projectData.tasks)
      ? projectData.tasks
      : [];
    const { updatedTasks } = evaluateTasks(projectTasks);

    const tasksChanged =
      projectTasks.length !== updatedTasks.length ||
      updatedTasks.some(
        (task, index) =>
          task.taskStatus !==
          (projectTasks[index] && projectTasks[index].taskStatus)
      );

    if (tasksChanged) {
      await updateDoc(projectDocRef, {
        tasks: updatedTasks,
        tasksSearchText: buildTasksSearchText(updatedTasks),
      });
    }

    const statusByTaskNo = new Map(
      updatedTasks.map((task) => [task.taskNo, task.taskStatus])
    );

    setTasks((prev) =>
      prev.map((task) =>
        task.projectId === projectId && statusByTaskNo.has(task.taskNo)
          ? { ...task, taskStatus: statusByTaskNo.get(task.taskNo) }
          : task
      )
    );
    setBaseTasks((prev) =>
      prev.map((task) =>
        task.projectId === projectId && statusByTaskNo.has(task.taskNo)
          ? { ...task, taskStatus: statusByTaskNo.get(task.taskNo) }
          : task
      )
    );
  };

  const handleEditCompletedOn = (task) => {
    setEditingCompletedOn({
      projectId: task.projectId,
      taskNo: task.taskNo,
      value: task.completedOn || "",
    });
  };

  const handleCompletedOnInputChange = (event) => {
    setEditingCompletedOn({ ...editingCompletedOn, value: event.target.value });
  };

  const handleCompletedOnInputBlur = async () => {
    const taskIndex = tasks.findIndex(
      (task) =>
        task.projectId === editingCompletedOn.projectId &&
        task.taskNo === editingCompletedOn.taskNo
    );

    if (taskIndex === -1) {
      return;
    }

    const updatedTasks = [...tasks];
    updatedTasks[taskIndex].completedOn = editingCompletedOn.value;
    setTasks(updatedTasks);
    setBaseTasks((prev) =>
      prev.map((task) =>
        task.projectId === editingCompletedOn.projectId &&
          task.taskNo === editingCompletedOn.taskNo
          ? { ...task, completedOn: editingCompletedOn.value }
          : task
      )
    );

    const projectDocRef = doc(db, "projects", editingCompletedOn.projectId);
    const projectDoc = await getDoc(projectDocRef);

    if (!projectDoc.exists()) {
      console.error("Project document does not exist");
      return;
    }

    const projectData = projectDoc.data();

    if (!projectData.tasks) {
      console.error("Project data does not contain tasks");
      return;
    }

    const updatedTasksArray = projectData.tasks.map((task) =>
      task.taskNo === editingCompletedOn.taskNo
        ? { ...task, completedOn: editingCompletedOn.value }
        : task
    );

    await updateDoc(projectDocRef, {
      tasks: updatedTasksArray.filter((task) => task !== undefined),
      tasksSearchText: buildTasksSearchText(
        updatedTasksArray.filter((task) => task !== undefined)
      ),
    });

    setEditingCompletedOn({ projectId: null, taskNo: null, value: "" });

    fetchAndRefreshTask(
      editingCompletedOn.projectId,
      editingCompletedOn.taskNo
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Stats calculations
  const stats = useMemo(() => {
    const source = baseTasks.length ? baseTasks : tasks;
    const inProgress = source.filter(
      (t) => t.taskStatus === "In Progress"
    ).length;
    const attention = source.filter((t) => t.taskStatus === "Attention").length;
    const overdue = source.filter((t) => t.taskStatus === "Overdue").length;
    const completed = source.filter((t) => t.taskStatus === "Completed").length;
    return { total: source.length, inProgress, attention, overdue, completed };
  }, [baseTasks, tasks]);

  // Task counts for filters
  const taskTypeCounts = useMemo(() => {
    const counts = {};
    taskOptions.forEach((t) => (counts[t] = 0));
    tasks.forEach((task) => {
      if (counts[task.task] !== undefined) {
        counts[task.task]++;
      }
    });
    return counts;
  }, [tasks]);

  // Available countries from baseTasks (sorted alphabetically)
  const availableCountries = useMemo(() => {
    const countries = new Set();
    baseTasks.forEach((task) => {
      if (task.country) {
        countries.add(task.country);
      }
    });
    return Array.from(countries).sort();
  }, [baseTasks]);

  // Country counts for filters
  const countryCounts = useMemo(() => {
    const counts = {};
    tasks.forEach((task) => {
      if (task.country) {
        counts[task.country] = (counts[task.country] || 0) + 1;
      }
    });
    return counts;
  }, [tasks]);

  // Status helpers
  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return "bg-green-100 text-green-800 border-green-200";
      case "Overdue":
        return "bg-red-100 text-red-800 border-red-200";
      case "Attention":
        return "bg-amber-100 text-amber-700 border-amber-200";
      case "In Progress":
        return "bg-blue-100 text-blue-800 border-blue-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Completed":
        return <CheckCircle className="w-4 h-4" />;
      case "Overdue":
        return <AlertCircle className="w-4 h-4" />;
      case "Attention":
        return <Clock className="w-4 h-4" />;
      case "In Progress":
        return <Activity className="w-4 h-4" />;
      default:
        return <Activity className="w-4 h-4" />;
    }
  };

  const getStatusDotColor = (status) => {
    switch (status) {
      case "Completed":
        return "bg-green-500";
      case "Overdue":
        return "bg-red-500";
      case "Attention":
        return "bg-amber-500";
      case "In Progress":
        return "bg-blue-500";
      default:
        return "bg-gray-500";
    }
  };

  const canViewFinancials = can(ACTIONS.FINANCIALS_VIEW);
  const canFilterAllAnalysts = can(ACTIONS.TASKS_FILTER_ALL_ANALYSTS);

  const analysts = [
    { value: "All", label: "All Analysts", restricted: !canFilterAllAnalysts },
    {
      value: "Zak",
      label: "Zak",
      restricted: !canFilterAllAnalysts && defaultAnalyst !== "Zak",
    },
    {
      value: "Omar",
      label: "Omar",
      restricted: !canFilterAllAnalysts && defaultAnalyst !== "Omar",
    },
    {
      value: "Muhab",
      label: "Muhab",
      restricted: !canFilterAllAnalysts && defaultAnalyst !== "Muhab",
    },
    {
      value: "Aseel",
      label: "Aseel",
      restricted: !canFilterAllAnalysts && defaultAnalyst !== "Aseel",
    },
    {
      value: "Mustafa",
      label: "Mustafa",
      restricted: !canFilterAllAnalysts && defaultAnalyst !== "Mustafa",
    },
    {
      value: "Sadegh",
      label: "Sadegh",
      restricted: !canFilterAllAnalysts && defaultAnalyst !== "Sadegh",
    },
  ];

  return (
    <div className="min-h-screen bg-white pb-12 pt-6 font-sans">
      <HeaderActionsMinimal />
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div
          className="rounded-2xl shadow-lg border border-gray-200 p-6 mb-8 relative overflow-hidden"
          style={{ background: theme.loginBackground }}
        >
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full mix-blend-overlay filter blur-3xl opacity-20 -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full mix-blend-overlay filter blur-3xl opacity-20 translate-y-1/2 -translate-x-1/2"></div>

          <div className="relative z-10 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
            <div className="text-white">
              <div className="flex items-center gap-4 mb-2">
                <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center backdrop-blur-sm border border-white/20">
                  <ListTodo className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold tracking-tight">
                    Tasks Dashboard
                  </h1>
                  <p className="text-blue-100 text-base">
                    {analyst === "All" ? "All tasks" : `${analyst}'s tasks`}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <StatCard
                title="Total"
                value={stats.total}
                icon={<ListTodo className="w-4 h-4" />}
                color="blue"
              />
              <StatCard
                title="In Progress"
                value={stats.inProgress}
                icon={<Activity className="w-4 h-4" />}
                color="blue"
              />
              <StatCard
                title="Attention"
                value={stats.attention}
                icon={<Clock className="w-4 h-4" />}
                color="yellow"
              />
              <StatCard
                title="Overdue"
                value={stats.overdue}
                icon={<AlertCircle className="w-4 h-4" />}
                color="red"
              />
              <StatCard
                title="Completed"
                value={stats.completed}
                icon={<CheckCircle className="w-4 h-4" />}
                color="green"
              />
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex gap-6">
          {/* Left Sidebar - Filters */}
          <div className="w-72 flex-shrink-0">
            <div className="sticky top-6">
              <div
                className="filter-panel-scroll bg-white rounded-2xl shadow-sm border border-gray-100/80 pt-6 px-6 pb-3 overflow-y-auto"
                style={{ maxHeight: "calc(100vh - 3rem)" }}
              >
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
                  <div className="flex items-center gap-2.5">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: theme.filterIconBg }}
                    >
                      <Filter className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-base font-semibold text-gray-900">
                      Filters
                    </h3>
                  </div>
                  <span className="text-xs text-gray-400 font-medium">
                    {loading ? "Loading..." : `${tasks.length} results`}
                  </span>
                </div>

                {/* Analyst Selection */}
                <FilterSection title="Analyst">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[11px] font-medium text-gray-500">
                      Include Project Manager
                    </span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={includeProjectManager}
                        onChange={handleIncludeProjectManagerChange}
                        aria-label="Include project manager in analyst filter"
                      />
                      <div
                        className="w-10 h-5 bg-gray-200 peer-checked:bg-indigo-500 rounded-full transition-colors duration-200"
                        style={
                          includeProjectManager
                            ? { backgroundColor: theme.filterAccent }
                            : {}
                        }
                      ></div>
                      <div className="absolute left-1 top-1 w-3 h-3 bg-white rounded-full shadow-sm transform transition-transform duration-200 peer-checked:translate-x-5"></div>
                    </label>
                  </div>
                  <select
                    value={analyst}
                    onChange={handleAnalystChange}
                    className="w-full px-3 py-2.5 bg-gray-50 border-0 rounded-lg text-sm text-gray-900 font-medium focus:outline-none focus:ring-2 focus:ring-offset-1 transition-all cursor-pointer appearance-none"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "right 0.75rem center",
                      backgroundSize: "1rem",
                      "--tw-ring-color": theme.filterAccent,
                    }}
                  >
                    {analysts
                      .filter((a) => !a.restricted)
                      .map((a) => (
                        <option key={a.value} value={a.value}>
                          {a.label}
                        </option>
                      ))}
                  </select>
                </FilterSection>

                {/* Status Filters */}
                <FilterSection title="Status">
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                    {Object.keys(selectedStatuses).map((status) => (
                      <CheckboxItem
                        key={status}
                        label={status}
                        checked={selectedStatuses[status]}
                        onChange={() => handleStatusChange(status)}
                        theme={theme}
                      />
                    ))}
                  </div>
                </FilterSection>

                {/* Task Type Filters */}
                <FilterSection title="Task Type">
                  <div className="flex items-center gap-3 mb-3">
                    <button
                      onClick={handleSelectAllTasks}
                      className="text-xs font-medium transition-colors hover:opacity-70"
                      style={{ color: theme.filterAccent }}
                    >
                      Select all
                    </button>
                    <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                    <button
                      onClick={handleDeselectAllTasks}
                      className="text-xs text-gray-400 hover:text-gray-600 font-medium transition-colors"
                    >
                      Clear all
                    </button>
                  </div>
                  <div className="task-type-scroll max-h-32 overflow-y-auto pr-2 space-y-0.5">
                    {taskOptions.map((taskOption) => (
                      <CheckboxItem
                        key={taskOption}
                        label={taskOption}
                        checked={selectedTasks[taskOption]}
                        onChange={() => handleTaskChange(taskOption)}
                        count={taskTypeCounts[taskOption]}
                        theme={theme}
                      />
                    ))}
                  </div>
                </FilterSection>

                {/* Country Filters */}
                {availableCountries.length > 0 && (
                  <FilterSection title="Country">
                    <div className="flex items-center gap-3 mb-3">
                      <button
                        onClick={handleSelectAllCountries}
                        className="text-xs font-medium transition-colors hover:opacity-70"
                        style={{ color: theme.filterAccent }}
                      >
                        Select all
                      </button>
                      <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                      <button
                        onClick={handleDeselectAllCountries}
                        className="text-xs text-gray-400 hover:text-gray-600 font-medium transition-colors"
                      >
                        Clear all
                      </button>
                    </div>
                    <div className="country-scroll max-h-40 overflow-y-auto pr-2 space-y-0.5">
                      {availableCountries.map((country) => (
                        <CheckboxItem
                          key={country}
                          label={
                            <span className="flex items-center gap-1.5">
                              <CountryFlagIcon country={country} size={16} />
                              <span>{country}</span>
                            </span>
                          }
                          checked={selectedCountries[country] || false}
                          onChange={() => handleCountryChange(country)}
                          count={countryCounts[country] || 0}
                          theme={theme}
                        />
                      ))}
                    </div>
                  </FilterSection>
                )}

                {/* Sorting */}
                <FilterSection title="Sort By" noBorder balanced>
                  <div className="flex items-center gap-3">
                    <select
                      value={sortColumn}
                      onChange={(e) => setSortColumn(e.target.value)}
                      className="flex-1 min-w-[160px] px-3 py-2.5 bg-gray-50 border-0 rounded-lg text-sm text-gray-900 font-medium focus:outline-none focus:ring-2 focus:ring-offset-1 transition-all cursor-pointer appearance-none"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "right 0.75rem center",
                        backgroundSize: "1rem",
                        "--tw-ring-color": theme.filterAccent,
                      }}
                    >
                      <option value="dueDate">Due Date</option>
                      <option value="projectRef">Project Ref</option>
                    </select>
                    <div className="flex items-center gap-1.5 p-1 bg-gray-100 rounded-lg flex-shrink-0">
                      <button
                        onClick={() => setSortOrder("asc")}
                        aria-label="Sort ascending"
                        className={`w-8 h-8 flex items-center justify-center rounded-md transition-all duration-200 ${sortOrder === "asc"
                          ? "bg-white shadow-sm"
                          : "text-gray-500 hover:text-gray-700"
                          }`}
                        style={
                          sortOrder === "asc"
                            ? { color: theme.filterSelectedText }
                            : {}
                        }
                      >
                        <ArrowUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setSortOrder("desc")}
                        aria-label="Sort descending"
                        className={`w-8 h-8 flex items-center justify-center rounded-md transition-all duration-200 ${sortOrder === "desc"
                          ? "bg-white shadow-sm"
                          : "text-gray-500 hover:text-gray-700"
                          }`}
                        style={
                          sortOrder === "desc"
                            ? { color: theme.filterSelectedText }
                            : {}
                        }
                      >
                        <ArrowDown className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </FilterSection>
              </div>
            </div>
          </div>

          {/* Right Content - Task Cards */}
          <div className="flex-1 min-w-0">
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((idx) => (
                  <div
                    key={idx}
                    className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 animate-pulse"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-gray-200 rounded-lg" />
                      <div className="flex-1 space-y-2">
                        <div className="h-3 bg-gray-200 rounded w-1/3" />
                        <div className="h-3 bg-gray-200 rounded w-2/3" />
                      </div>
                      <div className="w-16 h-3 bg-gray-200 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            ) : tasks.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
                <ListTodo className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No tasks found
                </h3>
                <p className="text-sm text-gray-500">
                  Try adjusting your filters to see more tasks.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {groupConsecutiveTasksByProject(tasks).map((group, groupIndex) => {
                  const taskCount = group.tasks.length;
                  // Height constants
                  const collapsedCardHeight = 60; // Collapsed card height
                  const expandedCardHeight = 265; // Approximate expanded card height (after removing View Project button)
                  const taskGap = 8;
                  const connectionWidth = 40;
                  const projectRefCardWidth = 110;
                  const projectRefCardHeight = 60;

                  // Calculate each card's height and cumulative positions
                  const cardHeights = group.tasks.map((task) => {
                    const taskId = `${task.projectId}-${task.taskNo}`;
                    return expandedTasks[taskId]
                      ? expandedCardHeight
                      : collapsedCardHeight;
                  });

                  // Calculate the top position of each card (cumulative heights + gaps)
                  const cardTopPositions = [];
                  let cumulativeHeight = 0;
                  cardHeights.forEach((height, idx) => {
                    cardTopPositions.push(cumulativeHeight);
                    cumulativeHeight +=
                      height + (idx < cardHeights.length - 1 ? taskGap : 0);
                  });

                  // Total height of the group
                  const totalGroupHeight = cumulativeHeight;

                  // Calculate where the line should connect (center of collapsed header, 30px from card top)
                  const lineConnectionPoints = cardTopPositions.map(
                    (topPos) => topPos + collapsedCardHeight / 2
                  );

                  // Calculate center point for the project ref card
                  const projectRefCenterY = totalGroupHeight / 2;

                  return (
                    <div
                      key={`group-${groupIndex}-${group.projectRef}`}
                      className="flex items-start gap-0"
                    >
                      {/* Project Reference Card with Connecting Lines */}
                      <div
                        className="flex-shrink-0 relative"
                        style={{ height: `${totalGroupHeight}px` }}
                      >
                        {/* Project Ref Card - vertically centered */}
                        <button
                          onClick={() =>
                            openProjectInNewTab(group.projectId)
                          }
                          className="absolute left-0 bg-white rounded-lg shadow-sm border border-gray-200 hover:border-indigo-300 hover:shadow-md transition-all duration-200 flex flex-col items-center justify-center cursor-pointer group z-10 px-2"
                          style={{
                            top: `${(totalGroupHeight - projectRefCardHeight) / 2
                              }px`,
                            width: `${projectRefCardWidth}px`,
                            height: `${projectRefCardHeight}px`,
                          }}
                        >
                          <span className="flex items-center gap-1 text-[13px] font-bold text-indigo-600 group-hover:text-indigo-800 transition-colors">
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                navigator.clipboard.writeText(group.projectRef);
                              }}
                              className="p-0.5 rounded hover:bg-indigo-100 transition-colors cursor-pointer"
                              title="Copy project ref"
                            >
                              <Copy className="w-2 h-2" />
                            </span>
                            {group.projectRef}
                          </span>
                          <span
                            className="text-[10px] text-gray-500 mt-0.5 max-w-full truncate text-center"
                            title={group.tasks[0]?.client || ""}
                          >
                            {group.tasks[0]?.client || ""}
                          </span>
                        </button>

                        {/* Connecting Lines - Beautiful curved bezier paths */}
                        <svg
                          className="absolute"
                          style={{
                            left: `${projectRefCardWidth}px`,
                            top: 0,
                            width: `${connectionWidth}px`,
                            height: `${totalGroupHeight}px`,
                          }}
                          viewBox={`0 0 ${connectionWidth} ${totalGroupHeight}`}
                          fill="none"
                        >
                          {group.tasks.map((_, taskIdx) => {
                            // Start point: right side of project ref card (vertically centered in the group)
                            const startX = 0;
                            const startY = projectRefCenterY;

                            // End point: left side of each task card's collapsed header
                            const endX = connectionWidth;
                            const endY = lineConnectionPoints[taskIdx];

                            // Check if this is essentially a horizontal line (for middle tasks in odd groups)
                            const isHorizontal = Math.abs(startY - endY) < 2;

                            // For horizontal lines, add a tiny curve bump to make it visible
                            // For curved lines, use normal bezier control points
                            const controlX1 = connectionWidth * 0.4;
                            const controlY1 = isHorizontal
                              ? startY - 3
                              : startY;
                            const controlX2 = connectionWidth * 0.6;
                            const controlY2 = isHorizontal ? endY - 3 : endY;

                            return (
                              <path
                                key={taskIdx}
                                d={`M ${startX} ${startY} C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${endX} ${endY}`}
                                stroke={`url(#gradient-${groupIndex})`}
                                strokeWidth="2"
                                strokeLinecap="round"
                                fill="none"
                              />
                            );
                          })}
                          {/* Gradient definition for beautiful lines - unique ID per group */}
                          <defs>
                            <linearGradient
                              id={`gradient-${groupIndex}`}
                              x1="0%"
                              y1="0%"
                              x2="100%"
                              y2="0%"
                            >
                              <stop offset="0%" stopColor="#a5b4fc" />
                              <stop offset="100%" stopColor="#c7d2fe" />
                            </linearGradient>
                          </defs>
                        </svg>
                      </div>

                      {/* Task Cards */}
                      <div
                        className="flex-1 min-w-0 space-y-2"
                        style={{
                          marginLeft: `${projectRefCardWidth + connectionWidth
                            }px`,
                        }}
                      >
                        {group.tasks.map((task) => {
                          const taskId = `${task.projectId}-${task.taskNo}`;
                          const isExpanded = expandedTasks[taskId];
                          const isOverdue = task.taskStatus === "Overdue";
                          const isAttention = task.taskStatus === "Attention";

                          return (
                            <div
                              key={taskId}
                              className="bg-white rounded-xl shadow-sm border border-gray-100 hover:border-gray-200 transition-all duration-200 overflow-hidden"
                            >
                              {/* Collapsed Header */}
                              <div
                                onClick={() => toggleTaskExpanded(taskId)}
                                className="flex items-center gap-4 p-4 cursor-pointer hover:bg-gray-50/50 transition-colors"
                                style={{
                                  minHeight: `${collapsedCardHeight}px`,
                                }}
                              >
                                {/* Status Indicator */}
                                <div
                                  className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${getStatusDotColor(
                                    task.taskStatus
                                  )} ${isOverdue || isAttention
                                    ? "animate-pulse"
                                    : ""
                                    }`}
                                />

                                {/* Task Number Badge */}
                                <span className="px-2 py-0.5 text-xs font-medium bg-indigo-50 text-indigo-600 rounded">
                                  #{task.taskNo}
                                </span>

                                {/* Task Type Badge */}
                                <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded">
                                  {task.task}
                                </span>

                                {/* Subject */}
                                <span className="text-sm text-gray-900 truncate flex-1 min-w-0 font-medium">
                                  {task.subject}
                                </span>

                                {/* Country Flag */}
                                <span className="hidden sm:block">
                                  <CountryFlagIcon
                                    country={task.country}
                                    size={20}
                                    title={task.country}
                                  />
                                </span>

                                {/* Due Date */}
                                <div className="flex items-center gap-1.5 text-sm text-gray-500 min-w-[100px]">
                                  <Calendar className="w-3.5 h-3.5" />
                                  <span>{formatDate(task.dueDate)}</span>
                                </div>

                                {/* Status Badge */}
                                <span
                                  className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                                    task.taskStatus
                                  )}`}
                                >
                                  {getStatusIcon(task.taskStatus)}
                                  {task.taskStatus}
                                </span>

                                {/* Expand/Collapse Icon */}
                                <button
                                  className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-all flex-shrink-0"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleTaskExpanded(taskId);
                                  }}
                                >
                                  {isExpanded ? (
                                    <ChevronDown className="w-4 h-4" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4" />
                                  )}
                                </button>
                              </div>

                              {/* Expanded Content */}
                              {isExpanded && (
                                <div className="border-t border-gray-100 bg-gray-50/50 p-5">
                                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {/* Project Info */}
                                    <div className="space-y-3">
                                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                                        <Briefcase className="w-3.5 h-3.5" />
                                        Project Info
                                      </h4>
                                      <div className="space-y-2 bg-white rounded-lg p-3 border border-gray-100">
                                        <div className="flex justify-between items-center gap-3">
                                          <span className="text-xs text-gray-500 flex-shrink-0">
                                            Client
                                          </span>
                                          <span
                                            className="text-sm font-medium text-gray-900 truncate max-w-[180px] text-right"
                                            title={task.client || "N/A"}
                                          >
                                            {task.client || "N/A"}
                                          </span>
                                        </div>
                                        <div className="flex justify-between items-center gap-3">
                                          <span className="text-xs text-gray-500 flex-shrink-0">
                                            PoC
                                          </span>
                                          <span
                                            className="text-sm font-medium text-gray-900 truncate max-w-[180px] text-right"
                                            title={task.poc || "N/A"}
                                          >
                                            {task.poc || "N/A"}
                                          </span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-xs text-gray-500">
                                            Task No
                                          </span>
                                          <span className="text-sm font-medium text-gray-900">
                                            {task.taskNo}
                                          </span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-xs text-gray-500">
                                            Country
                                          </span>
                                          <span className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                                            <CountryFlagIcon
                                              country={task.country}
                                              size={18}
                                            />
                                            {task.country}
                                          </span>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Team & Dates */}
                                    <div className="space-y-3 flex flex-col h-full">
                                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                                        <User className="w-3.5 h-3.5" />
                                        Team & Schedule
                                      </h4>
                                      <div className="space-y-2 bg-white rounded-lg p-3 border border-gray-100 flex-1">
                                        <div className="flex justify-between">
                                          <span className="text-xs text-gray-500">
                                            Analyst 1
                                          </span>
                                          <span className="text-sm font-medium text-gray-900">
                                            {task.analyst1 || "N/A"}
                                          </span>
                                        </div>
                                        {task.analyst2 && (
                                          <div className="flex justify-between">
                                            <span className="text-xs text-gray-500">
                                              Analyst 2
                                            </span>
                                            <span className="text-sm font-medium text-gray-900">
                                              {task.analyst2}
                                            </span>
                                          </div>
                                        )}
                                        <div className="flex justify-between">
                                          <span className="text-xs text-gray-500">
                                            Accepted
                                          </span>
                                          <span className="text-sm font-medium text-gray-900">
                                            {formatDate(task.orderAccepted)}
                                          </span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-xs text-gray-500">
                                            Due Date
                                          </span>
                                          <span
                                            className={`text-sm font-medium ${isOverdue
                                              ? "text-red-600"
                                              : isAttention
                                                ? "text-yellow-600"
                                                : "text-gray-900"
                                              }`}
                                          >
                                            {formatDate(task.dueDate)}
                                          </span>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Completion & Financials */}
                                    <div className="space-y-3 flex flex-col h-full">
                                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                                        <CheckCircle className="w-3.5 h-3.5" />
                                        Completion
                                      </h4>
                                      <div className="space-y-2 bg-white rounded-lg p-3 border border-gray-100 flex-1">
                                        <div className="flex justify-between items-center">
                                          <span className="text-xs text-gray-500">
                                            Completed On
                                          </span>
                                          {editingCompletedOn.projectId ===
                                            task.projectId &&
                                            editingCompletedOn.taskNo ===
                                            task.taskNo ? (
                                            <input
                                              type="date"
                                              value={editingCompletedOn.value}
                                              onChange={
                                                handleCompletedOnInputChange
                                              }
                                              onBlur={
                                                handleCompletedOnInputBlur
                                              }
                                              autoFocus
                                              className="text-sm border-b border-indigo-500 focus:outline-none bg-transparent w-32"
                                            />
                                          ) : (
                                            <span
                                              onClick={() =>
                                                handleEditCompletedOn(task)
                                              }
                                              className={`text-sm font-medium cursor-pointer hover:text-indigo-600 transition-colors ${task.completedOn
                                                ? "text-green-600"
                                                : "text-gray-400"
                                                }`}
                                            >
                                              {task.completedOn
                                                ? formatDate(task.completedOn)
                                                : "Click to set"}
                                            </span>
                                          )}
                                        </div>

                                        {/* Financials - only for privileged users */}
                                        {canViewFinancials && (
                                          <>
                                            <div className="border-t border-gray-100 pt-2 mt-2">
                                              <div className="flex justify-between">
                                                <span className="text-xs text-gray-500">
                                                  Fee
                                                </span>
                                                <span className="text-sm font-medium text-gray-900">
                                                  £{task.income || "0"}
                                                </span>
                                              </div>
                                              <div className="flex justify-between">
                                                <span className="text-xs text-gray-500">
                                                  VAT
                                                </span>
                                                <span className="text-sm font-medium text-gray-900">
                                                  {task.vat || "0"}%
                                                </span>
                                              </div>
                                              <div className="flex justify-between">
                                                <span className="text-xs text-gray-500">
                                                  Total Inc VAT
                                                </span>
                                                <span className="text-sm font-bold text-indigo-600">
                                                  £{task.amountIncVat || "0"}
                                                </span>
                                              </div>
                                            </div>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                }
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tasks;
